import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { SupportTicketConstants } from '../../../constants/support-ticket';

export interface AddTicketAttachmentModalData {
  ticketId: number;
}

export interface TicketAttachmentResult {
  file: File;
  isPhi: boolean;
  dataEnrichmentDocumentType: string;
  dataEnrichmentDocumentDate: string;
}

@Component({
  selector: 'app-add-ticket-attachment-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    FormsModule
  ],
  templateUrl: './add-ticket-attachment-modal.component.html',
  styleUrl: './add-ticket-attachment-modal.component.css',
  providers: [provideNativeDateAdapter()]
})
export class AddTicketAttachmentModalComponent implements OnInit {
  attachmentForm: FormGroup;
  selectedFile: File | null = null;
  fileError: string | null = null;
  isDragOver = false;

  readonly dataEnrichmentDocumentTypes = SupportTicketConstants.DOCUMENT_TYPES;
  readonly today = new Date();

  constructor(
    public dialogRef: MatDialogRef<AddTicketAttachmentModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddTicketAttachmentModalData,
    private fb: FormBuilder
  ) {
    this.attachmentForm = this.fb.group({
      description: [''],
      isPhi: [false],
      dataEnrichmentDocumentType: ['', Validators.required],
      dataEnrichmentDocumentTypeOther: [''],
      dataEnrichmentDocumentDate: ['', Validators.required]
    });
  }

  ngOnInit(): void {}

  get isOtherDocumentType(): boolean {
    return this.attachmentForm.get('dataEnrichmentDocumentType')?.value === 'Other';
  }

  get isFormValid(): boolean {
    if (!this.selectedFile || this.fileError || !this.attachmentForm.valid) return false;
    if (this.isOtherDocumentType && !this.attachmentForm.value.dataEnrichmentDocumentTypeOther?.trim()) return false;
    return true;
  }

  onFileSelected(event: any): void {
    const file = event.target.files?.[0];
    if (file) {
      this.processFile(file);
      event.target.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.processFile(file);
    }
  }

  private processFile(file: File): void {
    if (!SupportTicketConstants.SUPPORTED_FILE_TYPES[file.type as keyof typeof SupportTicketConstants.SUPPORTED_FILE_TYPES]) {
      this.fileError = `File type "${file.type}" is not supported`;
      this.selectedFile = null;
      return;
    }

    if (file.size > SupportTicketConstants.MAX_FILE_SIZE) {
      this.fileError = `File size (${this.formatFileSize(file.size)}) exceeds the maximum limit of ${this.formatFileSize(SupportTicketConstants.MAX_FILE_SIZE)}`;
      this.selectedFile = null;
      return;
    }

    this.selectedFile = file;
    this.fileError = null;
  }

  removeFile(): void {
    this.selectedFile = null;
    this.fileError = null;
  }

  formatDate(date: Date | string | null): string {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toISOString().split('T')[0];
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  onSubmit(): void {
    if (this.isFormValid && this.selectedFile) {
      const result: TicketAttachmentResult = {
        file: this.selectedFile,
        isPhi: this.attachmentForm.value.isPhi,
        dataEnrichmentDocumentType: this.isOtherDocumentType
          ? this.attachmentForm.value.dataEnrichmentDocumentTypeOther
          : this.attachmentForm.value.dataEnrichmentDocumentType,
        dataEnrichmentDocumentDate: this.formatDate(this.attachmentForm.value.dataEnrichmentDocumentDate)
      };
      this.dialogRef.close(result);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
